import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2 } from "lucide-react";

// The eight required consents. Order and wording match the signed off copy and the
// SIGNUP_CONSENT_LABELS dictionary in backend/server.py. If you change a word here, change it
// there too and bump the version, because the backend hashes that wording into the record.
const REQUIRED_BOXES = [
  {
    key: "age_owner",
    title: "I am 18 or older, and I am the pet owner or an authorized caregiver.",
  },
  {
    key: "agreements",
    title: "I agree to the Terms of Service, the Privacy Policy, the Client Authorization Agreement, and the Shipping, Refund, Cancellation and Returns Policy.",
    links: true,
  },
  {
    key: "data_use",
    title: "I authorize PawLinx to collect, use, store, and share my information.",
    body: "This covers my name, date of birth, contact details, address, pet information, uploaded prescriptions, veterinarian information, order information, payment status, shipping status, and support communications.",
  },
  {
    key: "partner_share",
    title: "I authorize PawLinx to share that information with licensed professional partners.",
    body: "This includes veterinarians, clinics, pharmacies, pharmacists, couriers, payment processors, technology providers, and customer support providers, only as needed to fulfil my request.",
  },
  {
    key: "vet_contact",
    title: "I authorize PawLinx to contact my veterinarian, clinic, pharmacy, or prescription issuer.",
    body: "This is so we can verify prescription details and confirm refill authorization.",
  },
  {
    key: "rx_genuine",
    title: "I confirm that any prescription I upload is accurate, current, genuine, lawfully obtained, and intended only for the pet named on it.",
  },
  {
    key: "not_pharmacy",
    title: "I understand PawLinx is not a pharmacy, a veterinary clinic, an emergency service, or a substitute for veterinary advice.",
  },
  {
    key: "service_messages",
    title: "I agree to receive service related emails, texts, calls, and account notifications.",
    body: "These cover verification, prescriptions, orders, payments, shipping, refills, safety notices, and support. They are service messages, not marketing.",
  },
];

const EMPTY_CONSENT = REQUIRED_BOXES.reduce((acc, b) => ({ ...acc, [b.key]: false }), {});

function ConsentRow({ testid, checked, onChange, title, children }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testid + "-label"}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 w-4 h-4 rounded border-paw-border accent-paw-primary flex-shrink-0"
        data-testid={testid}
      />
      <span className="font-manrope text-xs text-paw-text-secondary leading-relaxed">
        <span className="block font-manrope font-medium text-[13px] text-paw-text">{title}</span>
        {children}
      </span>
    </label>
  );
}

const BG_IMG = "https://images.unsplash.com/photo-1768523973627-c4f74779b24f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxkb2clMjByZWxheGluZyUyMGhvbWV8ZW58MHx8fHwxNzc1OTM4MjYyfDA&ixlib=rb-4.1.0&q=85&w=800";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", confirmPassword: "", first_name: "", last_name: "", phone: "", dob: "", sms_consent: false, marketing_email: false });
  const [consent, setConsent] = useState(EMPTY_CONSENT);
  const allRequiredTicked = REQUIRED_BOXES.every((b) => consent[b.key]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) { navigate("/dashboard"); return null; }
  const update = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) { setError("Passwords do not match"); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (!form.dob) { setError("Please enter your date of birth"); return; }
    if (!allRequiredTicked) { setError("Please tick all of the required agreements below before creating your account"); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.first_name, form.last_name, form.phone, form.sms_consent, {
        dob: form.dob,
        marketing_email: form.marketing_email,
        consent,
      });
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[calc(100vh-64px)] flex">
      {/* Left, image */}
      <div className="hidden lg:block lg:w-[45%] relative">
        <img src={BG_IMG} alt="Cute pug resting" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-paw-primary/40 to-transparent" />
        <div className="absolute bottom-12 left-10 right-10">
          <p className="font-outfit font-bold text-2xl text-white mb-2">Join thousands of<br />pet parents saving.</p>
          <p className="font-manrope text-sm text-white/70">Create your account in under a minute. Start saving today.</p>
        </div>
      </div>
      {/* Right, form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 lg:py-16">
        <div className="w-full max-w-[420px]">
          <div className="mb-8">
            <h1 className="font-outfit font-bold text-3xl text-paw-text tracking-tight mb-2">Create your account</h1>
            <p className="font-manrope text-sm text-paw-text-muted">Track orders, save pet profiles, and reorder easily.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-paw-error/8 border border-paw-error/15 text-paw-error text-sm font-manrope rounded-xl px-4 py-3" data-testid="register-error">{error}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-manrope text-sm font-medium text-paw-text">First Name</Label>
                <Input required value={form.first_name} onChange={update("first_name")} placeholder="Jane" className="h-11 rounded-xl border-paw-border" data-testid="register-first-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-manrope text-sm font-medium text-paw-text">Last Name</Label>
                <Input required value={form.last_name} onChange={update("last_name")} placeholder="Doe" className="h-11 rounded-xl border-paw-border" data-testid="register-last-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="font-manrope text-sm font-medium text-paw-text">Date of birth</Label>
              <Input type="date" required value={form.dob} onChange={update("dob")} className="h-11 rounded-xl border-paw-border" data-testid="register-dob" />
              <p className="font-manrope text-xs text-paw-text-muted leading-relaxed">
                We ask for this to confirm your identity on prescription related calls and to meet our record keeping obligations. It is never shown publicly and never sold.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="font-manrope text-sm font-medium text-paw-text">Email</Label>
              <Input type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className="h-11 rounded-xl border-paw-border" data-testid="register-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-manrope text-sm font-medium text-paw-text">Phone <span className="text-paw-text-muted font-normal">(optional)</span></Label>
              <Input type="tel" value={form.phone} onChange={update("phone")} placeholder="+1 (555) 123-4567" className="h-11 rounded-xl border-paw-border" data-testid="register-phone" />
            </div>
            <p className="font-manrope text-xs text-paw-text-muted leading-relaxed">
              PawLinx may contact you by email, phone, SMS text, or account notification for service related purposes, including account verification, prescription issues, missing information, order updates, payment issues, shipping updates, refill reminders, safety notices, and support follow up.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-manrope text-sm font-medium text-paw-text">Password</Label>
                <Input type="password" required value={form.password} onChange={update("password")} placeholder="Min 6 chars" className="h-11 rounded-xl border-paw-border" data-testid="register-password" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-manrope text-sm font-medium text-paw-text">Confirm</Label>
                <Input type="password" required value={form.confirmPassword} onChange={update("confirmPassword")} placeholder="Confirm" className="h-11 rounded-xl border-paw-border" data-testid="register-confirm-password" />
              </div>
            </div>
            {/* Required consents. Never pre-ticked: a pre-ticked box is not consent, and the
                backend rejects the signup unless every one of these arrives explicitly true. */}
            <div className="rounded-xl border border-paw-border bg-paw-surface-alt/40 px-4 py-4 space-y-3.5 mt-2" data-testid="register-consent-block">
              <p className="font-manrope text-xs text-paw-text-secondary leading-relaxed">
                Before creating your PawLinx account, please review and accept the required agreements below. These consents allow PawLinx to process your pet prescription request, coordinate verification with licensed professional partners, and communicate with you about your account, prescription, order, refill, payment, shipping, safety, and support issues.
              </p>
              <div className="h-px bg-paw-border" />
              {REQUIRED_BOXES.map((b) => (
                <ConsentRow
                  key={b.key}
                  testid={"register-consent-" + b.key}
                  checked={!!consent[b.key]}
                  onChange={(e) => setConsent({ ...consent, [b.key]: e.target.checked })}
                  title={b.title}
                >
                  {b.links && (
                    <span className="block mt-1">
                      Read the <Link to="/terms" className="text-paw-accent font-medium hover:underline">Terms of Service</Link>, <Link to="/privacy" className="text-paw-accent font-medium hover:underline">Privacy Policy</Link>, <Link to="/client-authorization" className="text-paw-accent font-medium hover:underline">Client Authorization Agreement</Link> and <Link to="/shipping-refund" className="text-paw-accent font-medium hover:underline">Shipping, Refund, Cancellation and Returns Policy</Link>.
                    </span>
                  )}
                  {b.body && <span className="block mt-1">{b.body}</span>}
                </ConsentRow>
              ))}
            </div>

            {/* Optional. Neither of these is required to create an account or place an order. */}
            <div className="rounded-xl border border-paw-border bg-white px-4 py-4 space-y-3.5" data-testid="register-optional-block">
              <p className="font-manrope text-xs font-semibold text-paw-text">Optional</p>
              <ConsentRow
                testid="register-marketing-email"
                checked={form.marketing_email}
                onChange={(e) => setForm({ ...form, marketing_email: e.target.checked })}
                title="Send me PawLinx news, savings, and pet health tips by email."
              >
                <span className="block mt-1">Consent is not required to create an account or place an order. You can unsubscribe at any time.</span>
              </ConsentRow>
              <ConsentRow
                testid="register-sms-consent"
                checked={form.sms_consent}
                onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
                title="Text me my verification codes and order updates."
              >
                <span className="block mt-1">
                  By ticking this box you agree to receive one-time passcodes and order status texts from PawLinx at the mobile number above. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe, HELP for help. See our <Link to="/sms-terms" className="text-paw-accent font-medium hover:underline">SMS Terms</Link> and <Link to="/privacy" className="text-paw-accent font-medium hover:underline">Privacy Policy</Link>.
                </span>
              </ConsentRow>
              <p className="font-manrope text-xs text-paw-text-muted leading-relaxed">
                Marketing consent is optional and is not required to create an account, upload a prescription, place an order, or use PawLinx services. You may unsubscribe from promotional emails or opt out of promotional texts at any time.
              </p>
            </div>

            <button type="submit" disabled={loading || !allRequiredTicked} className="w-full bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl h-11 font-manrope font-semibold text-sm transition-colors flex items-center justify-center gap-2 mt-2" data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account and Submit Consent <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="font-manrope text-xs text-paw-text-muted leading-relaxed text-center">
              By creating your account you confirm that you have read and accepted the agreements ticked above. We store a dated record of exactly what you accepted.
            </p>
          </form>

          <p className="text-center mt-8 font-manrope text-sm text-paw-text-muted">
            Already have an account? <Link to="/login" className="text-paw-accent font-semibold hover:underline" data-testid="register-login-link">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
